import React, { useState, useEffect } from 'react';
import { navigate, useLocation } from '@reach/router';
import styled from 'styled-components';
import Text from 'components/Text';
import Logo from 'components/Logo';
import SetNewPasswordForm from 'components/SetNewPasswordForm';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { verifyAccount, verifyTokenMatches } from 'api/auth';
import { ReactComponent as LoaderIcon } from 'assets/loader-icon.svg';

const VerifyAccountPage = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background: #e8eaeb;
`;

const VerifyAccountPanel = styled.div`
  width: 400px;
  background-color: #ffffff;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 80px;
  padding: 35px 45px 25px;
  display: flex;
  flex-direction: column;
  align-content: center;
  text-align: center;
`;

const VerifyAccountHeader = styled.div`
  display: inline-block;
  margin: 20px;
`;

const Subheader = styled(Text)`
  font-size: 15px;
  margin: 2em auto;
  margin-bottom: 0;
  max-width: 300px;
  text-align: left;
`;

const ErrorMessage = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: #ea6e6d;
  padding-bottom: 1em;
  white-space: pre;
`;

const VerifyAccount = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [verificationError, setVerificationError] = useState(null);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const emailParam = queryParams.get('email');
  const tokenParam = queryParams.get('token');

  useEffect(() => {
    if (!emailParam || !tokenParam) {
      navigateToLogin();
      return;
    }
    const loginToken = async () => {
      //Check if the URL token and email match what is in the database so
      //that a user can access the page.
      //Expect a 200 response for matching, or a 4xx for unmatched.
      try {
        await verifyTokenMatches(emailParam, tokenParam);
      } catch (error) {
        navigateToLogin();
        return;
      }

      setIsLoading(false);
      setEmail(emailParam);
      setToken(tokenParam);
    };

    loginToken();
  }, [emailParam, tokenParam]);

  /**
   * Navigate to login when processes finish. Sometimes reach-router does not
   * have the ability to navigate immediately and gets stopped.
   */
  const navigateToLogin = () => {
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  const setPasswords = async password => {
    try {
      await verifyAccount(email, token, password);
      setShowSuccessModal(true);
    } catch (err) {
      const UNEXPECTED_ERROR_MESSAGE =
        'An error occurred while attempting to verify your account.\nPlease try again, or contact a system administrator if the problem persists.';
      setVerificationError(UNEXPECTED_ERROR_MESSAGE);
    }
  };

  return (
    <VerifyAccountPage>
      <VerifyAccountPanel>
        <Logo
          width="110px"
          height="50px"
          display="inline-block"
          margin="0.5em auto"
        />
        <VerifyAccountHeader>
          <Text fontSize="22px">Verify Account</Text>
          <Subheader>
            Please set a password to complete the account verification.
          </Subheader>
        </VerifyAccountHeader>

        {isLoading ? <LoaderIcon style={{ margin: '0 auto' }} /> : null}

        {!isLoading ? (
          <SetNewPasswordForm
            onSubmit={setPasswords}
            isError={verificationError ? true : false}
          />
        ) : null}

        {verificationError ? (
          <React.Fragment>
            <ErrorMessage>{verificationError}</ErrorMessage>
          </React.Fragment>
        ) : null}
      </VerifyAccountPanel>

      {showSuccessModal ? (
        <ConfirmationModal
          updateModalState={navigateToLogin}
          header="Account Verification"
          message="Your account has been successfully verified! Please login to continue."
        />
      ) : null}
    </VerifyAccountPage>
  );
};

export default VerifyAccount;
